import React, {FC, useMemo, useState} from "react";
import { MobileProposalTableProps } from "../lib/type";
import {
  Card,
  Divider,
  IconButton,
  List,
  ListItem,
  Stack,
  Typography,
} from "@mui/material";
import { WebShopIcon } from "../../WebShopIcon";
import { Button } from "../../Button";
import { isEmpty, isNil, isNumber } from "lodash";
import { MobileColumnItem } from "./MobileColumnItem";
import { MobileRecaculateColumnDrawer } from "./MobileRecaculateColumnDrawer";
import { getColumnCell, getFieldName } from "../lib/utils";
import { MobileColumnCard } from "./MobileColumnCard";
import { strings } from "@/src/shared/strings";
import { PdfLinkCell } from "./PdfLinkCell";
import { HintArea } from "../../HintArea";
import { useProposalTableContext } from "./ProposalTableProvider";
import { useFormContext } from "react-hook-form";

export const MobileProposalTable: FC = () => {
  const {
    proposalData,
    activeColumnId,
    rows,
    columns,
    pdfFile,
    openedHintAreaIndex,
    toggleHintArea,
    selectedColumnIndex,
    editingColumnIndex,
    onSelectColumnCard,
    onChangeCover,
    onHintAreaDetailsButtonPressed,
  } = useProposalTableContext();
  const {getValues} = useFormContext()
  const [isChangeCoverPending, setIsChangeCoverPending] = useState<number | null>(null);

  const isEditing = !!(
    isNumber(editingColumnIndex) &&
    isNumber(selectedColumnIndex) &&
    editingColumnIndex === selectedColumnIndex
  );
  return (
    <Stack className="hidden w-full flex-col gap-4 max-mdPlus:flex">
      {!pdfFile.hidden && (
        <Card>
          <PdfLinkCell
            iconName={pdfFile?.icon?.name}
            iconText={pdfFile?.title}
            url={pdfFile?.srcUrl}
          />
        </Card>
      )}
      <List className="grid w-full grid-cols-3 gap-2">
        {columns?.map((column, columnIndex) => {
          const isSelected = selectedColumnIndex === columnIndex;
          const handleChose = async () => {
            setIsChangeCoverPending(columnIndex);
            await onSelectColumnCard(columnIndex, column.title);
            setIsChangeCoverPending(null);
          };

          return (
            <ListItem key={column?.title ?? columnIndex}>
              <MobileColumnCard
                title={column?.title}
                isSelected={isSelected}
                onClick={handleChose}
                loading={isChangeCoverPending === columnIndex}
                pointerEventsNone={isChangeCoverPending !== null}
                price={column?.price}
                icon={column?.icon}
              />
            </ListItem>
          );
        })}
      </List>
      <Card className="p-4">
        {rows?.map((row, rowIndex) => {
          const columnCell = getColumnCell({
            proposalData,
            rowKey: row?.key,
            columnIndex: selectedColumnIndex,
            daysAbroad: getValues("daysAbroad")
          });
          const isHintAreaVisible = openedHintAreaIndex === rowIndex;
          const isHintButtonVisible = !!row?.hint;
          const isHinAreaDetailsButtonVisible =
            !!row?.hint?.hintCardDetailsButton;

          const hintAreaContentDescription = row.hint?.contentDescription ?? "";
          const hintDetailsTitle = row.hint?.hintDetailsTitle ?? "";
          const hintDetailsDescription = row.hint?.hintDetailsDescription ?? "";
          const hintDetailsActions =
            (row.hint?.hintDetailsActions as any) ?? [];

          const renderFieldItem = columnCell && !isEmpty(columnCell?.fields);

          return (
            <Stack className="flex flex-col" key={rowIndex}>
              <Stack className="justify-between flex-center-row">
                <Typography
                  className="max-w-52 max-[400px]:max-w-44"
                  variant="body1"
                >
                  {row?.title}
                </Typography>
                <Stack className="gap-1 flex-center-row max-[400px]:flex-wrap max-[400px]:justify-end">
                  {renderFieldItem ? (
                    columnCell.fields.map((fieldItem) => {
                      if (activeColumnId) {
                        const name = getFieldName({
                          columnId: activeColumnId,
                          fieldName: fieldItem.name,
                        });
                        return (
                          <MobileColumnItem
                            key={name}
                            name={name}
                            fieldItem={fieldItem}
                            hint={row?.hint}
                          />
                        );
                      }
                      return null;
                    })
                  ) : (
                    <Typography>{strings.common.noData}</Typography>
                  )}
                  {isHintButtonVisible && (
                    <IconButton
                      className="ml-1 flex"
                      onClick={() => toggleHintArea(rowIndex)}
                    >
                      <WebShopIcon name="questionCircle" />
                    </IconButton>
                  )}
                </Stack>
              </Stack>
              {isHintAreaVisible && (
                <HintArea
                  className="mt-2"
                  hintAreaDetailsButton={isHinAreaDetailsButtonVisible}
                  onHintAreaDetailsButtonPressed={() =>
                    onHintAreaDetailsButtonPressed({
                      title: hintDetailsTitle,
                      description: hintDetailsDescription,
                      actions: hintDetailsActions,
                    })
                  }
                >
                  {hintAreaContentDescription}
                </HintArea>
              )}
              {rowIndex !== rows.length - 1 && <Divider className="my-4" />}
            </Stack>
          );
        })}
        <Button
          variant="outlined"
          className="mt-4 w-full"
          onClick={() =>
            !isNil(selectedColumnIndex) &&
            !isNil(activeColumnId) &&
            onChangeCover(selectedColumnIndex, activeColumnId)
          }
        >
          {strings.proposalTable.changeCover}
        </Button>
        <MobileRecaculateColumnDrawer
          activeColumnIndex={editingColumnIndex}
          isOpen={isEditing}
          proposalData={proposalData}
        />
      </Card>
    </Stack>
  );
};
